import { useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useAccount } from '@/account/useAccount';
import { useSession } from '@/auth/index';
import { useAppConfigContext, useAppConfigRedirect } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { SearchFormProps, SearchTabData } from '@/reports/index';
import { BrandLogo, Collapse, Button, Stack } from '@/theme/index';
import { checkSingleReportVariations } from '@/utils/checkSingleReportVariations';
import { HeaderContents } from './HeaderContents/HeaderContents';
import { MobileHeaderSearch } from './MobileHeaderSearch';
import { AppHeader } from './components';
import { subscribe, unsubscribe } from '../../events';
import { SearchFormsContainer } from '../SearchFormsContainer/SearchFormsContainer';

interface HeaderProps {
  /** Optional callback to handle successful search form submission */
  onSearchSuccess?: SearchFormProps['onSuccess'];
  /** Optional callback to handle search form submission errors */
  onSearchError?: SearchFormProps['onError'];
  /** Callback to handle logout */
  handleLogout: () => void;
  showMobilePrimarySearchFormTabs?: boolean;
  combinedPropertySearch?: boolean;
  enableSmartSearch?: boolean;
}
/**
 * This is the header component for the application.
 * It contains the logo and search forms.
 */
export function Header({
  onSearchSuccess,
  onSearchError,
  handleLogout,
  showMobilePrimarySearchFormTabs = true,
  combinedPropertySearch,
  enableSmartSearch = false,
}: HeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    session: { isAuthenticated, account },
  } = useSession();
  const {
    config: { searchTabs },
    featureFlags: { navigationHeaderEnabled },
    variationPlanNames,
    internationalPlanNames,
  } = useAppConstantsContext();
  const [searchFormsIsMounted, setSearchFormsIsMounted] = useState(false);
  const [selectedTab, setSelectedTab] = useState<SearchTabData | null>(null);
  const { redirect } = useAppConfigRedirect();
  const { location } = useAppConfigContext().routingUtils;
  const isInOnboardingRoute = location?.pathname.includes('/onboarding');
  const isInSelfSearchingRoute = location?.pathname.includes('/self-searching');
  const isInSelfInspectionRoute = location?.pathname.includes(
    '/vehicle-self-inspection'
  );

  const { data: accountData } = useAccount(true);

  const reportVariations =
    accountData && internationalPlanNames
      ? checkSingleReportVariations(
          accountData,
          variationPlanNames,
          internationalPlanNames
        )
      : null;
  const isInSingleReportVariation =
    reportVariations?.isInSingleReportVariation ?? false;
  const isInInternationalLanguageTest =
    reportVariations?.isInInternationalLanguageTest ?? false;
  const isInternationalVinTest =
    reportVariations?.isInternationalVinTest ?? false;

  const queryContext = useQueryClient();
  const searchErrorHandler =
    onSearchError ||
    (((error: unknown, variables: unknown, context: unknown) => {
      closeSearchForms();
      queryContext
        ?.getDefaultOptions()
        ?.mutations?.onError?.(error, variables, context);
    }) as SearchFormProps['onError']);

  const setTab = (tab: SearchTabData) => {
    const isDifferentTab = !selectedTab || tab.id !== selectedTab.id;
    const newTab = isDifferentTab ? tab : null;
    setSearchFormsIsMounted(isDifferentTab);
    setSelectedTab(newTab);
  };

  useEffect(() => {
    subscribe('openPropertySearch', () => {
      const propertyTab = searchTabs.find(
        (tab) => tab.displayName === 'Property'
      );
      if (propertyTab) {
        setSelectedTab(propertyTab);
        setSearchFormsIsMounted(true);
      }
    });
    subscribe('openPersonSearch', () => {
      const peopleTab = searchTabs.find((tab) => tab.displayName === 'People');
      if (peopleTab) {
        setSelectedTab(peopleTab);
        setSearchFormsIsMounted(true);
      }
      if (navigationHeaderEnabled) {
        redirect('/search/contact');
      }
    });
    subscribe('openPhoneSearch', () => {
      const phoneTab = searchTabs.find((tab) => tab.displayName === 'Phone');
      if (phoneTab) {
        setSelectedTab(phoneTab);
        setSearchFormsIsMounted(true);
      }
    });
    subscribe('openVehicleSearch', () => {
      const vehicleTab = searchTabs.find(
        (tab) => tab.displayName === 'Vehicle'
      );
      if (vehicleTab) {
        setSearchFormsIsMounted(true);
        setSelectedTab(vehicleTab);
      }
    });

    return () => {
      unsubscribe('openPropertySearch', () => {
        setSelectedTab(null);
        setSearchFormsIsMounted(false);
      });
      unsubscribe('openPersonSearch', () => {
        setSelectedTab(null);
        setSearchFormsIsMounted(false);
      });
      unsubscribe('openVehicleSearch', () => {
        setSelectedTab(null);
        setSearchFormsIsMounted(false);
      });
      unsubscribe('openPhoneSearch', () => {
        setSelectedTab(null);
        setSearchFormsIsMounted(false);
      });
      unsubscribe('openPhoneSearch', () => {
        setSelectedTab(null);
        setSearchFormsIsMounted(false);
      });
    };
  }, [searchTabs]);

  const headerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const allowedElements = [
        '#select-options-container',
        '.MuiAutocomplete-popper',
        '#vin-scanner',
        '#notistack-snackbar',
      ];
      const clickOnAllowedElement = allowedElements.some((selector) =>
        (event.target as Element)?.closest(selector)
      );
      if (
        headerRef.current &&
        !(headerRef.current as Element).contains(event.target as Node) &&
        !clickOnAllowedElement
      ) {
        closeSearchForms();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [headerRef]);

  const closeSearchForms = () => {
    setSearchFormsIsMounted(false);
    setSelectedTab(null);
  };

  const toggleSearchFormsIsMounted = () => {
    setSearchFormsIsMounted((prevState) => !prevState);
  };

  const handleSearchSuccess: SearchFormProps['onSuccess'] = (searchPayload) => {
    onSearchSuccess && onSearchSuccess(searchPayload);
    closeSearchForms();
  };

  const accountInPassedDueState = () => {
    const renewalDate = new Date(
      account?.account?.subscription_info?.normalize_date || ''
    );
    const currentDate = new Date();
    const accountIsPastDue = renewalDate.getTime() < currentDate.getTime();
    const hasReactivationNotification =
      account?.account?.notification?.type === 'reactivation';
    const nonCompliant = account?.account?.user_info?.noncompliant;

    return accountIsPastDue && hasReactivationNotification && !nonCompliant;
  };

  function handleBrandLogoClick() {
    if (accountInPassedDueState()) {
      redirect('/reactivation');
      return;
    }

    if (account?.account?.user_info?.noncompliant || isInSelfInspectionRoute) {
      return;
    }

    if (isInInternationalLanguageTest) {
      redirect('/dashboard/language');
      return;
    }

    if (isInternationalVinTest) {
      redirect('/dashboard/reports');
      return;
    }

    redirect('/dashboard');
  }

  const shouldLockSearchForm =
    isInSingleReportVariation && selectedTab?.isLocked;

  const shouldHideSearchForm =
    isInInternationalLanguageTest && !isInternationalVinTest;

  return (
    <div ref={headerRef}>
      <AppHeader id="header">
        <Stack>
          <Button
            disabled={isInSelfInspectionRoute}
            disableRipple
            sx={{
              height: 'fit-content',
              py: 1.875,
              backgroundColor: 'transparent',
              '&.MuiButtonBase-root:hover': {
                bgcolor: 'transparent',
              },
            }}
            onClick={handleBrandLogoClick}
            data-testid="brand-logo-btn"
          >
            <BrandLogo />
          </Button>
          {!isInSelfSearchingRoute && !isInSelfInspectionRoute && (
            <HeaderContents
              handleLogout={handleLogout}
              selectedTab={selectedTab}
              setSelectedTab={setTab}
              toggleSearchFormsIsMounted={toggleSearchFormsIsMounted}
              enableSmartSearch={enableSmartSearch}
              shouldHideSearchForm={shouldHideSearchForm}
              isInternationalVinTest={isInternationalVinTest}
              isInInternationalLanguageTest={isInInternationalLanguageTest}
            />
          )}
        </Stack>
      </AppHeader>

      {isAuthenticated && !isInOnboardingRoute && !navigationHeaderEnabled && (
        <>
          {isMobile ? (
            <MobileHeaderSearch
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              onSearchSuccess={handleSearchSuccess}
              onSearchError={searchErrorHandler}
              closeForm={closeSearchForms}
              searchFormsIsMounted={searchFormsIsMounted}
              showMobilePrimarySearchFormTabs={showMobilePrimarySearchFormTabs}
              combinedPropertySearch={combinedPropertySearch}
              isSearchFormLocked={shouldLockSearchForm}
              shouldHideSearchForm={shouldHideSearchForm}
            />
          ) : (
            <Collapse in={searchFormsIsMounted} timeout={500} unmountOnExit>
              <SearchFormsContainer
                displayName={selectedTab?.displayName || ''}
                internalTabs={selectedTab?.internalTabs}
                onSearchSuccess={handleSearchSuccess}
                onSearchError={searchErrorHandler}
                closeForm={closeSearchForms}
                placement="header"
                combinedPropertySearch={combinedPropertySearch}
                isSearchFormLocked={shouldLockSearchForm}
                shouldHideSearchForm={shouldHideSearchForm}
                isInternationalVinTest={isInternationalVinTest}
              />
            </Collapse>
          )}
        </>
      )}
    </div>
  );
}
